import { type CSSProperties, type KeyboardEvent } from 'react'
import type { CalendarLeave } from '@lms/shared-types'
import { calendarName } from '../../lib/display-name'
import { leaveTypeStyle } from '../../lib/leave-type-palette'
import {
  LeaveDayBreakdownFromCalendar,
  countDayRows,
  hasDayBreakdown,
  useCalendarLeaveDayRows,
} from '../employee/LeaveDayBreakdown'
import { Icon, type IconName } from './Icon'
import { LeaveBarTooltip, type LeaveBarTooltipProps } from './LeaveBarTooltip'
import { Tooltip } from './Tooltip'

export interface LeaveBarProps {
  leave: CalendarLeave
  /** True when the bar's left edge is at the start of its range (else clipped at the week boundary). */
  startsHere: boolean
  /** True when the bar's right edge is at the end of its range (else clipped). */
  endsHere: boolean
  /** `comfortable` (default, 22px) is the calendar page; `compact` (16px) is for palette widgets. */
  variant?: 'compact' | 'comfortable'
  /** `low` (default) shows icon + initials + label. `high` shows initials only. */
  density?: 'low' | 'high'
  /** Adds a clay outline + warning icon prefix. The reason flows into LeaveBarTooltip. */
  conflict?: 'blackout'
  /** Inset terracotta ring — visual cue that this leave is on the viewer's own team. */
  ownTeam?: boolean
  /** Optional click handler. When set, the bar becomes a keyboard-activatable button. */
  onClick?: () => void
  /** Optional tooltip enrichments forwarded to LeaveBarTooltip. */
  conflictReason?: string
  /**
   * Overrides the leave-type-derived fill/border/text so the bar can be
   * coloured by something else (e.g. the person's team). Falls back to the
   * leave-type palette when omitted.
   */
  colorStyle?: { bg: string; line: string; ink: string }
  /** Hides the leading leave-type icon (the bar reads as name + label only). */
  hideIcon?: boolean
  /**
   * How the person is labelled: `initials` (default, "AC" in a chip) or
   * `name` ("Aisyah R" — first name + last-name initial, plain text).
   */
  nameFormat?: 'initials' | 'name'
  /**
   * Overrides the trailing type label. `undefined` keeps the leave-type label
   * (e.g. "Annual"); a string shows that text (e.g. "Annual Leave"); `null`
   * hides the label entirely (used for privacy-masked leaves — name only).
   */
  typeLabel?: string | null
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase()
}

export function LeaveBar({
  leave,
  startsHere,
  endsHere,
  variant = 'comfortable',
  density = 'low',
  conflict,
  ownTeam = false,
  onClick,
  conflictReason,
  colorStyle,
  hideIcon = false,
  nameFormat = 'initials',
  typeLabel,
}: Readonly<LeaveBarProps>): JSX.Element {
  const style = leaveTypeStyle(leave.type)
  const barBg = colorStyle?.bg ?? style.bg
  const barLine = colorStyle?.line ?? style.line
  const barInk = colorStyle?.ink ?? style.ink
  // `undefined` → default leave-type label; explicit string/null overrides it.
  const shownTypeLabel = typeLabel !== undefined ? typeLabel : style.label
  const fontSize = variant === 'compact' ? 10 : 11
  const iconSize = variant === 'compact' ? 12 : 14
  const isPending = leave.status === 'pending'
  const interactive = onClick !== undefined

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (!interactive) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick?.()
    }
  }

  const radius: CSSProperties = {
    borderTopLeftRadius: startsHere ? 10 : 0,
    borderBottomLeftRadius: startsHere ? 10 : 0,
    borderTopRightRadius: endsHere ? 10 : 0,
    borderBottomRightRadius: endsHere ? 10 : 0,
  }

  const tooltipConflictReason =
    conflict === 'blackout' && conflictReason === undefined
      ? 'Blackout window'
      : conflictReason

  // LeaveBarTooltip.status is narrowed to 'approved' | 'pending' — the pending
  // hatch visual applies to those two states; any other status (draft, rejected,
  // etc.) is treated as non-pending in the tooltip display.
  const tooltipStatus: 'approved' | 'pending' | undefined =
    leave.status === 'approved' || leave.status === 'pending'
      ? leave.status
      : undefined

  // Show the day-by-day breakdown in the tooltip when the leave type gets a
  // working-day breakdown at all (`halfDayAllowed`, sent per-leave so this
  // works even when the type is privacy-masked) and the leave actually spans
  // more than one full day or carries a half. The breakdown is built from the
  // calendar payload (no detail fetch) and reveals only the day structure —
  // never the type or reason — so it's safe for any leave the viewer can see.
  const showBreakdown = hasDayBreakdown(leave)

  return (
    <Tooltip
      content={
        <>
          <TooltipHeader
            leave={leave}
            countWorkingDays={showBreakdown}
            employeeName={leave.employeeName}
            leaveType={leave.type}
            startDate={leave.startDate}
            endDate={leave.endDate}
            halfDay={leave.halfDay}
            teamName={leave.teamName}
            {...(tooltipStatus !== undefined ? { status: tooltipStatus } : {})}
            {...(tooltipConflictReason !== undefined
              ? { conflictReason: tooltipConflictReason }
              : {})}
            {...(showBreakdown ? { hideDateRange: true } : {})}
            {...(!showBreakdown && leave.reason !== undefined && leave.reason !== null
              ? { reason: leave.reason }
              : {})}
          />
          {showBreakdown ? (
            <LeaveDayBreakdownFromCalendar leave={leave} tone="tooltip" />
          ) : null}
        </>
      }
      placement="top"
      // A long leave (e.g. 80+ days) makes the breakdown taller than the
      // viewport; make the bubble hoverable + scrollable so it can be read.
      interactive={showBreakdown}
    >
      <div
        data-testid="leave-bar"
        data-leave-type={leave.type}
        data-conflict={conflict ?? undefined}
        data-own-team={ownTeam ? 'true' : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        style={{
          position: 'absolute',
          inset: 0,
          background: barBg,
          // Pending leaves keep their solid team colour (no opacity wash, which
          // read as "transparent"). The "not yet approved" cue is a 1.5px dashed
          // border in the darker team hue.
          border: `${isPending ? 1.5 : 1}px ${isPending ? 'dashed' : 'solid'} ${conflict !== undefined ? 'var(--danger-line)' : barLine}`,
          outline: conflict !== undefined ? '1.5px solid var(--danger-line)' : undefined,
          outlineOffset: -1.5,
          ...radius,
          color: barInk,
          fontFamily: 'var(--font-mono, inherit)',
          fontSize,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          paddingLeft: variant === 'compact' ? 6 : 8,
          paddingRight: variant === 'compact' ? 6 : 8,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          cursor: interactive ? 'pointer' : 'default',
          boxShadow: ownTeam ? 'inset 0 0 0 1px var(--steel-blue)' : undefined,
          pointerEvents: 'auto',
        }}
      >
        {conflict !== undefined ? (
          <span data-testid="leave-bar-conflict-icon">
            <Icon name="warning" size={iconSize} color="var(--danger-fg)" />
          </span>
        ) : null}
        {density === 'low' && !hideIcon ? (
          <Icon name={style.icon as IconName} size={iconSize} color={barInk} />
        ) : null}
        {nameFormat === 'name' ? (
          // Preferred name in full, else first-name + last-initial; plain
          // bold text (no chip).
          <span
            style={{
              fontWeight: 700,
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {calendarName({
              name: leave.employeeName,
              preferredName: leave.employeePreferredName ?? null,
            })}
          </span>
        ) : (
          <span
            style={{
              fontWeight: 700,
              letterSpacing: '0.04em',
              flexShrink: 0,
              // Calendar bars wrap the initials in a small light chip so the
              // person reads as an avatar-style badge; palette widgets (compact)
              // keep the bare initials.
              ...(variant === 'comfortable'
                ? {
                    background: 'var(--surface)',
                    color: barInk,
                    borderRadius: 5,
                    padding: '0 4px',
                    fontSize: 9.5,
                    lineHeight: '15px',
                  }
                : {}),
            }}
          >
            {initialsOf(leave.employeeName)}
          </span>
        )}
        {density === 'low' && shownTypeLabel ? (
          <span
            style={{
              opacity: 0.85,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {shownTypeLabel}
          </span>
        ) : null}
      </div>
    </Tooltip>
  )
}

type TooltipHeaderProps = LeaveBarTooltipProps & {
  leave: CalendarLeave
  /** Replace the calendar-day span with the breakdown's working-day count. */
  countWorkingDays: boolean
}

/** Tooltip header for a calendar bar. When the day-by-day breakdown is shown
 *  below it, the "· N days" count comes from the same working-day rows, so
 *  weekends and public holidays are never counted in the header but skipped
 *  in the list. */
function TooltipHeader({ leave, countWorkingDays, ...props }: TooltipHeaderProps): JSX.Element {
  if (!countWorkingDays) return <LeaveBarTooltip {...props} />
  return <WorkingDayTooltipHeader leave={leave} {...props} />
}

function WorkingDayTooltipHeader({
  leave,
  ...props
}: LeaveBarTooltipProps & { leave: CalendarLeave }): JSX.Element {
  const { rows } = useCalendarLeaveDayRows(leave)
  return <LeaveBarTooltip {...props} dayCount={countDayRows(rows)} />
}
